/**
 * @typedef {import ('./BlogIndexState').default} State *
 * @typedef {import ('./BlogIndexProps').default} Props *
 * @typedef {import ('./PostExcerpt').default} PostExcerpt
 */

import { translate } from 'App/Internationalization';
import BaseHero from 'shared/BaseHero';
import BlogPostExcerptCard from 'shared/BlogPostExcerptCard';
import BlogSearchForm from 'Blog/BlogSearchForm';
import captureError from '../utils/captureError';
import images from 'config/local/images';
import Loading from '../shared/Loading';
import NoContent from '../shared/NoContent';
import Pagination from 'shared/Pagination';
import React from 'react';

/** @extends	{React.Component<Props, State>} */
export default class BlogIndex extends React.Component {
	/** @param {Props} props */
	constructor(props) {
		super(props);

		/** @type {State} */
		this.state = {
			currentPageNumber: 1,
			errorMessage: '',
			isGettingData: true,
			lastPageNumber: null,
			lastSearchQuery: '',
			postExcerptList: [],
		};

		this.handleSearchFormSubmit = this.handleSearchFormSubmit.bind(this);
		this.setCurrentPageNumber = this.setCurrentPageNumber.bind(this);
	}

	/** @returns {Promise<void>} */
	async componentDidMount() {
		await this.getAndSetPostExcerptList();
	}

	/**
	 * @param {string} [searchQuery]
	 * @param {number} [pageNumber]
	 * @returns {Promise<void>}
	 */
	async getAndSetPostExcerptList(
		searchQuery = this.state.lastSearchQuery,
		pageNumber = this.state.currentPageNumber
	) {
		this.setState({ isGettingData: true });

		try {
			const result = await this.props.blogService.getPostExcerpts(
				searchQuery ?? '',
				pageNumber
			);

			this.setState({
				currentPageNumber: pageNumber,
				errorMessage: '',
				lastPageNumber: result.totalPages,
				lastSearchQuery: searchQuery ?? '',
				postExcerptList: result.posts,
			});
		} catch (error) {
			captureError(error);
			this.setState({
				errorMessage: translate(
					'homeowner.pages.dashboard.marketplace.error'
				),
			});
		} finally {
			this.setState({ isGettingData: false });
		}
	}

	/**
	 * @param {{searchQuery: string}} values
	 * @returns {Promise<void>}
	 */
	async handleSearchFormSubmit(values) {
		const { searchQuery } = values;
		if (!searchQuery && this.state.lastSearchQuery === searchQuery) {
			return;
		}

		await this.getAndSetPostExcerptList(searchQuery, 1);
	}

	/**
	 * @param {number} pageNumber
	 */
	async setCurrentPageNumber(pageNumber) {
		this.setState({ currentPageNumber: pageNumber });
		await this.getAndSetPostExcerptList('', pageNumber);
	}

	/** @returns {JSX.Element} */
	getHeader() {
		return (
			<React.Fragment>
				<BaseHero
					heroImageUrl={images.blog.header}
					title={translate('global.blog.title')}
				/>
				<BlogSearchForm
					disabled={
						this.state.errorMessage || this.state.isGettingData
					}
					onSubmit={this.handleSearchFormSubmit}
				/>
			</React.Fragment>
		);
	}

	render() {
		if (this.state.errorMessage) {
			return (
				<React.Fragment>
					{this.getHeader()}
					<NoContent message={this.state.errorMessage} />
				</React.Fragment>
			);
		}

		if (this.state.isGettingData) {
			return (
				<React.Fragment>
					{this.getHeader()}
					<Loading />
				</React.Fragment>
			);
		}

		const postExcerptCardList = this.state.postExcerptList.map(
			(postExcerpt) => (
				<BlogPostExcerptCard
					agentEmail={
						this.props.theme.agentEmail ?? postExcerpt.author.email
					}
					agentSlug={
						this.props.theme.agentSlug ?? postExcerpt.author.slug
					}
					authorName={
						this.props.theme.agentName ?? postExcerpt.author.name
					}
					content={postExcerpt.content}
					date={postExcerpt.date}
					imageUrl={postExcerpt.imageUrl}
					key={postExcerpt.title}
					slug={postExcerpt.slug}
					title={postExcerpt.title}
				/>
			)
		);

		return (
			<main className="mb-6">
				{this.getHeader()}
				<section className="listing listing-col-size-3 mt-12">
					{postExcerptCardList}
				</section>
				<Pagination
					currentPageNumber={this.state.currentPageNumber}
					lastPageNumber={this.state.lastPageNumber ?? 1}
					setCurrentPageNumber={this.setCurrentPageNumber}
				/>
			</main>
		);
	}
}
